'use client';

import { useMobileAppLinks } from '@/hooks/useMobileAppsLinks';
import clsx from 'clsx';
import Image from 'next/image';
import Link from 'next/link';
import { Skeleton } from '@/components/ui/skeleton';

type MobileAppStoreLinksProps = {
  downloadLabel: string;
  iosLabel: string;
  androidLabel: string;
  containerClassName?: string;
  buttonClassName?: string;
};

const baseContainerClass = 'flex gap-[16px] min-w-[300px]';
const baseButtonClass =
  'rounded-[120px] px-[12px] bg-primary-green-day h-[48px] flex gap-[8px] items-center justify-center min-w-[140px]';

export function MobileAppStoreLinks({
  downloadLabel,
  iosLabel,
  androidLabel,
  containerClassName = baseContainerClass,
  buttonClassName = '',
}: MobileAppStoreLinksProps) {
  const { isLoading, hasError, data } = useMobileAppLinks();
  const buttonClasses = `${baseButtonClass} ${buttonClassName}`.trim();

  if (isLoading) {
    return (
      <div className={containerClassName}>
        <Skeleton className={buttonClasses} />
        <Skeleton className={buttonClasses} />
      </div>
    );
  }

  if (hasError || !data) {
    return null;
  }

  const iosHref = typeof data.iosAppUrl === 'string' ? data.iosAppUrl : undefined;
  const androidHref = typeof data.androidAppUrl === 'string' ? data.androidAppUrl : undefined;

  if (!iosHref && !androidHref) {
    return null;
  }

  return (
    <div className={containerClassName}>
      {iosHref ? (
        <Link target='_blank' rel='noopener noreferrer' href={iosHref} className={buttonClasses}>
          <Image src='/assets/icons/icon-apple.svg' height={24} width={24} alt='apple' />
          <div>
            <div className='text-[10px] font-normal text-white'>{downloadLabel}</div>
            <div className='text-[16px] font-medium text-white'>{iosLabel}</div>
          </div>
        </Link>
      ) : null}

      {androidHref ? (
        <Link
          target='_blank'
          rel='noopener noreferrer'
          href={androidHref}
          className={clsx(buttonClasses, 'min-w-[150px]')}
        >
          <Image src='/assets/icons/icon-google.svg' height={19} width={19} alt='google' />
          <div>
            <div className='text-[10px] font-normal text-white'>{downloadLabel}</div>
            <div className='text-[16px] font-medium text-white'>{androidLabel}</div>
          </div>
        </Link>
      ) : null}
    </div>
  );
}
